import { z } from 'zod';

import { ZodCurrency, ZodIntegerAmountValue } from '@Common/types/Amount';
import { ZodB2bCampaignType } from '@Common/types/bo/b2b/campaigns';
import { BoListQueryBase } from '@Common/types/bo/bo_list_query';
import { buildZodBoListQueryParsed } from '@Common/types/bo/utils/build_zod_bo_list_query';
import { ZodDate } from '@Common/types/date';
import { ZodCoCardTopUpsStatus } from '@Common/types/funding/deposit';
import { ZodMdlrPaymentStatus } from '@Common/types/modulr';
import { ZodIDPathParam, ZodId } from '@Common/types/request';
import { createZodArrayable } from '@Common/types/zod/zod-arrayable';
import { endpoint, GetEndpointType } from '@Common/types/zod/zod-endpoint';
import { createZodNumberLike } from '@Common/types/zod/zod-number-like';
import { createZodParamsValidator } from '@Common/types/zod/zod-params';
import { ZodTrimmedString } from '@Common/types/zod/zod-trimmed-string';

export const SavingFundsRequestStatusValues = [
  'check_employer_balance',
  'promote_orders',
  'funds_request_paid',
  'funds_request_completed',
  'finished',
  'cancelled',
  'check_employer_balance_failed',
  'promote_orders_failed',
  'funds_request_paid_failed',
  'funds_request_completed_failed',
  'finished_failed',
  'cancelled_failed',
  'funds_request_pending',
] as const;
export const ZodSavingFundsRequestStatus = z.enum(SavingFundsRequestStatusValues);
export type SavingFundsRequestStatus = z.TypeOf<typeof ZodSavingFundsRequestStatus>;

export const SavingsFundsRequestTypeBoValues = [
  'matching_contribution',
  'b2b_management_fee',
  'b2b_custody_fee',
  'campaign',
  'ccb_withdrawal',
  'capital_increase',
] as const;
export const ZodSavingsFundsRequestType = z.enum(SavingsFundsRequestTypeBoValues);
export type SavingsFundsRequestType = z.TypeOf<typeof ZodSavingsFundsRequestType>;

export const SavingsFundsRequestReconStatusValues = [
  'balanced',
  'over_paid',
  'under_paid',
] as const;
export const ZodSavingsFundsRequestReconStatus = z.enum(SavingsFundsRequestReconStatusValues);
export type SavingsFundsRequestReconStatus = z.TypeOf<typeof ZodSavingsFundsRequestReconStatus>;

const ZodSavingsFundsRequestBase = z.object({
  id: ZodId,
  b2b_company_id: ZodId,
  date: z.date(),
  total_net_amount: z.number(),
  total_gross_amount: z.number(),
  number_of_eligible_employees: z.number(),
  currency: ZodCurrency,
  created_at: z.date(),
  updated_at: z.date(),
  status: ZodSavingFundsRequestStatus.nullable(),
  type: ZodSavingsFundsRequestType,
});

export const ZodSavingsFundsRequestsListItem = z.object({
  ...ZodSavingsFundsRequestBase.shape,
  recon_status: ZodSavingsFundsRequestReconStatus,
});

export type SavingsFundsRequestsListItem = z.TypeOf<typeof ZodSavingsFundsRequestsListItem>;

export const ZodSavingsFundsRequestsListResponse = z.array(ZodSavingsFundsRequestsListItem);

export type SavingsFundsRequestsListResponse = z.TypeOf<typeof ZodSavingsFundsRequestsListResponse>;

export type SavingsFundsRequestsDisplayListItem = Omit<SavingsFundsRequestsListItem, 'currency'>;

export const getSavingsFundsRequestsSortColumns: (keyof SavingsFundsRequestsDisplayListItem)[] = [
  'id',
  'total_net_amount',
  'total_gross_amount',
  'b2b_company_id',
  'number_of_eligible_employees',
  'date',
  'created_at',
  'updated_at',
  'type',
  'status',
  'recon_status',
];

const ZodSavingsFundsRequestsFilters = z
  .object({
    ...ZodSavingsFundsRequestsListItem.pick({
      number_of_eligible_employees: true,
    }).shape,
    id: createZodArrayable(ZodId),
    b2b_company_id: createZodArrayable(ZodId),
    type: createZodArrayable(ZodSavingsFundsRequestType),
    from_created_at: ZodTrimmedString,
    to_created_at: ZodTrimmedString,
    total_gross_amount: createZodNumberLike(z.number()),
    total_net_amount: createZodNumberLike(z.number()),
    status: createZodArrayable(ZodSavingFundsRequestStatus),
    recon_status: createZodArrayable(ZodSavingsFundsRequestReconStatus),
  })
  .partial();

type SavingsFundsRequestsFilters = z.TypeOf<typeof ZodSavingsFundsRequestsFilters>;

const ZodSavingsFundsRequestsQuery = buildZodBoListQueryParsed(
  ZodSavingsFundsRequestsFilters,
  getSavingsFundsRequestsSortColumns,
  { range: [0, 100] },
);

export type GetSavingsFundsRequestsListQuery = BoListQueryBase<
  SavingsFundsRequestsFilters,
  (typeof getSavingsFundsRequestsSortColumns)[number]
>;

export const getSavingsFundsRequestsEndpoint = endpoint({
  method: 'GET',
  queryParams: ZodSavingsFundsRequestsQuery,
  responseBody: ZodSavingsFundsRequestsListResponse,
});

export type GetSavingsFundsRequestsEndpoint = GetEndpointType<
  typeof getSavingsFundsRequestsEndpoint
>;

export const RetryableSepaDirectDebitCoPaymentStatusValues = [
  'rejected',
  'returned',
  'authorization_declined',
  'pending_declined',
  'shares_cancelled',
] as const;
export const ZodRetryableSepaDirectDebitCoPaymentStatus = z.enum(
  RetryableSepaDirectDebitCoPaymentStatusValues,
);
export type RetryableSepaDirectDebitCoPaymentStatus = z.TypeOf<
  typeof ZodRetryableSepaDirectDebitCoPaymentStatus
>;

const ZodSavingsFundsRequestBaseExtended = z.object({
  ...ZodSavingsFundsRequestBase.shape,
  company_name: ZodTrimmedString,
  total_contribution_amount: z.number(),
  number_of_eligible_employees: z.number(),
  is_marked_as_paid: z.boolean(),
  is_marked_as_completed: z.boolean(),
  is_marked_as_cancelled: z.boolean(),
  co_payment_status: ZodCoCardTopUpsStatus.nullable(),
  is_sepa_direct_debit_retryable: z.boolean(),
  payment_reference: ZodTrimmedString.nullable(),
});

const ZodCampaignSavingsFundsRequestDetails = z.object({
  ...ZodSavingsFundsRequestBaseExtended.shape,
  type: z.literal('campaign'),
  campaign_id: ZodId.nullable(),
  campaign_type: ZodB2bCampaignType.nullable(),
  ts_user_total_nb_eligible: z.number().int().nullable(),
  tns_user_total_nb_eligible: z.number().int().nullable(),
  ts_user_total_gross_amount: z.number().int().nullable(),
  tns_user_total_gross_amount: z.number().int().nullable(),
  ts_user_total_net_amount: z.number().int().nullable(),
  tns_user_total_net_amount: z.number().int().nullable(),
  ts_user_total_csg_tax_amount: z.number().int().nullable(),
  tns_user_total_csg_tax_amount: z.number().int().nullable(),
  ts_user_total_crds_tax_amount: z.number().int().nullable(),
  tns_user_total_crds_tax_amount: z.number().int().nullable(),
  ts_user_total_contribution_amount: z.number().int().nullable(),
  tns_user_total_contribution_amount: z.number().int().nullable(),
});

const ZodOtherSavingsFundsRequestDetails = z.object({
  ...ZodSavingsFundsRequestBaseExtended.shape,
  b2b_withdrawal_id: ZodId.nullable(),
  type: z.enum([
    'matching_contribution',
    'b2b_management_fee',
    'b2b_custody_fee',
    'ccb_withdrawal',
    'capital_increase',
  ]),
  ts_user_total_nb_eligible: z.number().int().nullable(),
  tns_user_total_nb_eligible: z.number().int().nullable(),
  ts_user_total_gross_amount: z.number().int().nullable(),
  tns_user_total_gross_amount: z.number().int().nullable(),
  ts_user_total_net_amount: z.number().int().nullable(),
  tns_user_total_net_amount: z.number().int().nullable(),
  ts_user_total_csg_tax_amount: z.number().int().nullable(),
  tns_user_total_csg_tax_amount: z.number().int().nullable(),
  ts_user_total_crds_tax_amount: z.number().int().nullable(),
  tns_user_total_crds_tax_amount: z.number().int().nullable(),
  ts_user_total_contribution_amount: z.number().int().nullable(),
  tns_user_total_contribution_amount: z.number().int().nullable(),
});

export const ZodSavingsFundsRequestDetails = z.discriminatedUnion('type', [
  ZodCampaignSavingsFundsRequestDetails,
  ZodOtherSavingsFundsRequestDetails,
]);

export type SavingsFundsRequestBaseExtended = z.TypeOf<typeof ZodSavingsFundsRequestBaseExtended>;
export type CampaignSavingsFundsRequestDetails = z.TypeOf<
  typeof ZodCampaignSavingsFundsRequestDetails
>;
export type OtherSavingsFundsRequestDetails = z.TypeOf<typeof ZodOtherSavingsFundsRequestDetails>;

export type SavingsFundsRequestDetails = z.TypeOf<typeof ZodSavingsFundsRequestDetails>;

const ZodSavingsFundsRequestRefundInfo = z.object({
  correction_orders_total_amount: ZodIntegerAmountValue,
  has_pending_correction_orders: z.boolean(),
  refunded_total_amount: ZodIntegerAmountValue,
});

export type SavingsFundsRequestRefundInfo = z.TypeOf<typeof ZodSavingsFundsRequestRefundInfo>;

/**
 * Reasons a fund request cannot be cancelled from the back office.
 * The back office maps them to a message shown when hovering the disabled "Cancel" button.
 */
export const SavingsFundsRequestCancelBlockerValues = [
  'unsupported_type',
  'already_cancelled',
  'already_paid',
  'linked_transfer_in_progress',
  'orders_not_pending_company',
  'in_flight_sepa_dd_payment',
  'released_sepa_dd_payment',
  'insufficient_refund_funds',
  'unfilled_aggregated_orders',
  'has_processed_managed_contributions',
  'in_flight_correction_orders',
] as const;

export const ZodSavingsFundsRequestCancelBlocker = z.enum(SavingsFundsRequestCancelBlockerValues);

export type SavingsFundsRequestCancelBlocker = z.TypeOf<typeof ZodSavingsFundsRequestCancelBlocker>;

const ZodSavingsFundsRequestCancelInfo = z.object({
  /**
   * `null` when the fund request can be cancelled.
   */
  cancel_blocker: ZodSavingsFundsRequestCancelBlocker.nullable(),
});

export type SavingsFundsRequestCancelInfo = z.TypeOf<typeof ZodSavingsFundsRequestCancelInfo>;

/**
 * Raw technical error of the last flow transition
 */
const ZodSavingsFundsRequestExecutionError = z.object({
  execution_error: ZodTrimmedString.nullable(),
});

export type SavingsFundsRequestExecutionError = z.TypeOf<
  typeof ZodSavingsFundsRequestExecutionError
>;

const ZodBoSavingsFundsRequestDetailsExtras = z.object({
  ...ZodSavingsFundsRequestRefundInfo.shape,
  ...ZodSavingsFundsRequestCancelInfo.shape,
  ...ZodSavingsFundsRequestExecutionError.shape,
});

export const ZodBoSavingsFundsRequestDetails = z.discriminatedUnion('type', [
  ZodCampaignSavingsFundsRequestDetails.extend(ZodBoSavingsFundsRequestDetailsExtras.shape),
  ZodOtherSavingsFundsRequestDetails.extend(ZodBoSavingsFundsRequestDetailsExtras.shape),
]);

export type BoSavingsFundsRequestDetails = z.TypeOf<typeof ZodBoSavingsFundsRequestDetails>;

export const getSavingsFundsRequestDetailsEndpoint = endpoint({
  method: 'GET',
  pathParams: ZodIDPathParam,
  responseBody: ZodBoSavingsFundsRequestDetails,
});

export type GetSavingsFundsRequestDetailsEndpoint = GetEndpointType<
  typeof getSavingsFundsRequestDetailsEndpoint
>;

const ZodSavingsFundsRequestEmployee = z.object({
  id: ZodId,
  employee_id: ZodId,
  firstname: ZodTrimmedString.nullable(),
  lastname: ZodTrimmedString.nullable(),
  identifier: ZodTrimmedString.nullable(),
  registration_number: ZodTrimmedString.nullable(),
  social_status: ZodTrimmedString.nullable(),
  currency: ZodCurrency,
  gross_amount: z.number().int(),
  net_amount: z.number().int(),
  contribution_amount: z.number().int(),
  csg_tax_amount: z.number().int(),
  crds_tax_amount: z.number().int(),
});

export type SavingsFundsRequestEmployee = z.TypeOf<typeof ZodSavingsFundsRequestEmployee>;

export const getSavingsFundsRequestEmployeesSortColumns = [
  'employee_id',
  'firstname',
  'lastname',
  'identifier',
  'registration_number',
  'social_status',
  'gross_amount',
  'net_amount',
  'contribution_amount',
] as const satisfies (keyof SavingsFundsRequestEmployee)[];

const ZodSavingsFundsRequestEmployeesFilters = z.object({
  savings_funds_request_id: ZodId,
  employee_id: createZodArrayable(ZodId).optional(),
  social_status: createZodArrayable(ZodTrimmedString).optional(),
});

type SavingsFundsRequestEmployeesFilters = z.TypeOf<typeof ZodSavingsFundsRequestEmployeesFilters>;

const ZodSavingsFundsRequestEmployeesQuery = buildZodBoListQueryParsed(
  ZodSavingsFundsRequestEmployeesFilters,
  getSavingsFundsRequestEmployeesSortColumns,
  { range: [0, 100] },
);

export type GetSavingsFundsRequestEmployeesQuery = BoListQueryBase<
  SavingsFundsRequestEmployeesFilters,
  (typeof getSavingsFundsRequestEmployeesSortColumns)[number]
>;

export const getSavingsFundsRequestEmployeesEndpoint = endpoint({
  method: 'GET',
  queryParams: ZodSavingsFundsRequestEmployeesQuery,
  responseBody: z.array(ZodSavingsFundsRequestEmployee),
});

export type GetSavingsFundsRequestEmployeesEndpoint = GetEndpointType<
  typeof getSavingsFundsRequestEmployeesEndpoint
>;

export const ZodSavingsFundsRequestAction = z.enum(['paid', 'completed']);
export type SavingsFundsRequestAction = z.infer<typeof ZodSavingsFundsRequestAction>;

export const ZodPutSavingsFundsRequest = z.object({
  action: ZodSavingsFundsRequestAction,
});
export type PutSavingsFundsRequest = z.infer<typeof ZodPutSavingsFundsRequest>;

const ZodPutSavingsFundsResponse = z.object({
  id: ZodId,
});

export const putSavingsFundsRequestEndpoint = endpoint({
  method: 'PUT',
  pathParams: ZodIDPathParam,
  body: ZodPutSavingsFundsRequest,
  responseBody: ZodPutSavingsFundsResponse,
});
export type PutSavingsFundsRequestEndpoint = GetEndpointType<typeof putSavingsFundsRequestEndpoint>;

export const ZodPutSavingsFundsRequestRetrySepaDirectDebit = z.object({});
export type PutSavingsFundsRequestRetrySepaDirectDebit = z.infer<
  typeof ZodPutSavingsFundsRequestRetrySepaDirectDebit
>;

const ZodPutSavingsFundsRequestRetrySepaDirectDebitResponse = z.object({
  id: ZodId,
  co_payment_id: ZodId,
});

export const putSavingsFundsRequestRetrySepaDirectDebitEndpoint = endpoint({
  method: 'PUT',
  pathParams: ZodIDPathParam,
  body: ZodPutSavingsFundsRequestRetrySepaDirectDebit,
  responseBody: ZodPutSavingsFundsRequestRetrySepaDirectDebitResponse,
});
export type PutSavingsFundsRequestRetrySepaDirectDebitEndpoint = GetEndpointType<
  typeof putSavingsFundsRequestRetrySepaDirectDebitEndpoint
>;

/**
 * `reason` is required to cancel a CCB withdrawal fund request
 */
export const ZodPutSavingsFundsRequestCancel = z.object({
  reason: ZodTrimmedString.min(1).optional(),
  description: ZodTrimmedString.optional(),
});
export type PutSavingsFundsRequestCancel = z.infer<typeof ZodPutSavingsFundsRequestCancel>;

const ZodPutSavingsFundsRequestCancelResponse = z.object({
  id: ZodId,
});

export const putSavingsFundsRequestCancelEndpoint = endpoint({
  method: 'PUT',
  pathParams: ZodIDPathParam,
  body: ZodPutSavingsFundsRequestCancel,
  responseBody: ZodPutSavingsFundsRequestCancelResponse,
});
export type PutSavingsFundsRequestCancelEndpoint = GetEndpointType<
  typeof putSavingsFundsRequestCancelEndpoint
>;

export const ZodPutSavingsFundsRequestRefresh = z.object({});

export type PutSavingsFundsRequestRefresh = z.infer<typeof ZodPutSavingsFundsRequestRefresh>;

const ZodPutSavingsFundsRequestRefreshResponse = z.object({
  id: ZodId,
});

export const putSavingsFundsRequestRefreshEndpoint = endpoint({
  method: 'PUT',
  pathParams: ZodIDPathParam,
  body: ZodPutSavingsFundsRequestRefresh,
  responseBody: ZodPutSavingsFundsRequestRefreshResponse,
});

export type PutSavingsFundsRequestRefreshEndpoint = GetEndpointType<
  typeof putSavingsFundsRequestRefreshEndpoint
>;

export const ZodPutSavingsFundsRequestRegenerateDocument = z.object({});

export type PutSavingsFundsRequestRegenerateDocument = z.infer<
  typeof ZodPutSavingsFundsRequestRegenerateDocument
>;

const ZodPutSavingsFundsRequestRegenerateDocumentResponse = z.object({
  id: ZodId,
});

export const putSavingsFundsRequestRegenerateDocumentEndpoint = endpoint({
  method: 'PUT',
  pathParams: ZodIDPathParam,
  body: ZodPutSavingsFundsRequestRegenerateDocument,
  responseBody: ZodPutSavingsFundsRequestRegenerateDocumentResponse,
});

export type PutSavingsFundsRequestRegenerateDocumentEndpoint = GetEndpointType<
  typeof putSavingsFundsRequestRegenerateDocumentEndpoint
>;

const EmployeeSavingsFundsRequestStateSuccessValues = [
  'check_employer_balance',
  'promote_orders',
  'funds_request_paid',
  'funds_request_completed',
  'finished',
  'cancelled',
] as const;
export type EmployeeSavingsFundsRequestStateSuccess =
  (typeof EmployeeSavingsFundsRequestStateSuccessValues)[number];
const EmployeeSavingsFundsRequestStatePendingValues = ['funds_request_pending'] as const;

const EmployeeSavingsFundsRequestStateFailValues = [
  'check_employer_balance_failed',
  'promote_orders_failed',
  'funds_request_paid_failed',
  'funds_request_completed_failed',
  'finished_failed',
  'cancelled_failed',
] as const;
export type EmployeeSavingsFundsRequestStateFail =
  (typeof EmployeeSavingsFundsRequestStateFailValues)[number];

export const EmployeeSavingsFundsRequestStateValues = [
  ...EmployeeSavingsFundsRequestStateSuccessValues,
  ...EmployeeSavingsFundsRequestStateFailValues,
  ...EmployeeSavingsFundsRequestStatePendingValues,
] as const;
export type EmployeeSavingsFundsRequestState =
  (typeof EmployeeSavingsFundsRequestStateValues)[number];

const ZodB2bCompanyTreasuryPayment = z.object({
  id: ZodId,
  amount: z.number(),
  currency: ZodCurrency,
  status: ZodMdlrPaymentStatus.nullable(),
  direction: z.enum(['in', 'out']),
  created_at: ZodDate,
});
export type B2bCompanyTreasuryPayment = z.TypeOf<typeof ZodB2bCompanyTreasuryPayment>;

const ZodB2bCompanyTreasurySfrItem = z.object({
  id: ZodId,
  b2b_company_id: ZodId,
  type: ZodSavingsFundsRequestType,
  date: z.date(),
  currency: ZodCurrency,
  total_net_amount: z.number(),
  status: ZodSavingFundsRequestStatus.nullable(),
  processed_sum: z.number(),
  pending_sum: z.number(),
  has_failed_final_payment: z.boolean(),
  payments: z.array(ZodB2bCompanyTreasuryPayment),
  correction_orders_total_amount: z.number(),
  has_pending_correction_orders: z.boolean(),
  refunded_total_amount: z.number(),
  is_sepa_direct_debit_retryable: z.boolean(),
  sepa_direct_debit_retry_blocker: z.string().nullable(),
});
export type B2bCompanyTreasurySfrItem = z.TypeOf<typeof ZodB2bCompanyTreasurySfrItem>;

export const getB2bCompanyTreasuryReconciliationSortColumns: (keyof B2bCompanyTreasurySfrItem)[] = [
  'id',
  'date',
  'total_net_amount',
  'type',
  'status',
];

const ZodB2bCompanyTreasuryReconciliationFilters = z
  .object({
    b2b_company_id: ZodId,
  })
  .partial();
type B2bCompanyTreasuryReconciliationFilters = z.TypeOf<
  typeof ZodB2bCompanyTreasuryReconciliationFilters
>;

const ZodB2bCompanyTreasuryReconciliationQuery = buildZodBoListQueryParsed(
  ZodB2bCompanyTreasuryReconciliationFilters,
  getB2bCompanyTreasuryReconciliationSortColumns,
  { range: [0, 24] },
);

export type GetB2bCompanyTreasuryReconciliationListQuery = BoListQueryBase<
  B2bCompanyTreasuryReconciliationFilters,
  (typeof getB2bCompanyTreasuryReconciliationSortColumns)[number]
>;

const ZodB2bCompanyTreasurySfrListResponse = z.array(ZodB2bCompanyTreasurySfrItem);

export const getB2bCompanyTreasuryReconciliationEndpoint = endpoint({
  method: 'GET',
  queryParams: ZodB2bCompanyTreasuryReconciliationQuery,
  responseBody: ZodB2bCompanyTreasurySfrListResponse,
});
export type GetB2bCompanyTreasuryReconciliationEndpoint = GetEndpointType<
  typeof getB2bCompanyTreasuryReconciliationEndpoint
>;

const ZodB2bCompanyTreasuryCandidatePayment = z.object({
  ...ZodB2bCompanyTreasuryPayment.shape,
  reference: ZodTrimmedString,
});
export type B2bCompanyTreasuryCandidatePayment = z.TypeOf<
  typeof ZodB2bCompanyTreasuryCandidatePayment
>;

export const getB2bCompanyTreasuryCandidatePaymentsSortColumns: (keyof B2bCompanyTreasuryCandidatePayment)[] =
  ['id', 'amount', 'created_at'];

const ZodB2bCompanyTreasuryCandidatePaymentsFilters = z
  .object({
    b2b_company_id: ZodId,
  })
  .partial();
type B2bCompanyTreasuryCandidatePaymentsFilters = z.TypeOf<
  typeof ZodB2bCompanyTreasuryCandidatePaymentsFilters
>;

const ZodGetB2bCompanyTreasuryCandidatePaymentsQuery = buildZodBoListQueryParsed(
  ZodB2bCompanyTreasuryCandidatePaymentsFilters,
  getB2bCompanyTreasuryCandidatePaymentsSortColumns,
  { range: [0, 999] },
);

export type GetB2bCompanyTreasuryCandidatePaymentsListQuery = BoListQueryBase<
  B2bCompanyTreasuryCandidatePaymentsFilters,
  (typeof getB2bCompanyTreasuryCandidatePaymentsSortColumns)[number]
>;

export const getB2bCompanyTreasuryCandidatePaymentsEndpoint = endpoint({
  method: 'GET',
  queryParams: ZodGetB2bCompanyTreasuryCandidatePaymentsQuery,
  responseBody: z.array(ZodB2bCompanyTreasuryCandidatePayment),
});
export type GetB2bCompanyTreasuryCandidatePaymentsEndpoint = GetEndpointType<
  typeof getB2bCompanyTreasuryCandidatePaymentsEndpoint
>;

const ZodB2bCompanyTreasuryLinkPaymentBody = z.object({
  mdlr_payment_id: ZodId,
  savings_funds_request_id: ZodId,
});
export type B2bCompanyTreasuryLinkPaymentBody = z.TypeOf<
  typeof ZodB2bCompanyTreasuryLinkPaymentBody
>;

const ZodPostB2bCompanyTreasuryLinkPaymentResponse = z.object({
  id: ZodId,
  success: z.literal(true),
});

export const postB2bCompanyTreasuryLinkPaymentEndpoint = endpoint({
  method: 'POST',
  pathParams: ZodIDPathParam,
  body: ZodB2bCompanyTreasuryLinkPaymentBody,
  responseBody: ZodPostB2bCompanyTreasuryLinkPaymentResponse,
});
export type PostB2bCompanyTreasuryLinkPaymentEndpoint = GetEndpointType<
  typeof postB2bCompanyTreasuryLinkPaymentEndpoint
>;

const ZodDeleteB2bCompanyTreasuryLinkQuery = createZodParamsValidator(
  z.object({
    mdlr_payment_id: ZodId,
    savings_funds_request_id: ZodId,
  }),
);
export type DeleteB2bCompanyTreasuryLinkQuery = z.TypeOf<
  typeof ZodDeleteB2bCompanyTreasuryLinkQuery
>;

const ZodDeleteB2bCompanyTreasuryLinkResponse = z.object({
  success: z.literal(true),
});

export const deleteB2bCompanyTreasuryLinkEndpoint = endpoint({
  method: 'DELETE',
  pathParams: ZodIDPathParam,
  queryParams: ZodDeleteB2bCompanyTreasuryLinkQuery,
  responseBody: ZodDeleteB2bCompanyTreasuryLinkResponse,
});
export type DeleteB2bCompanyTreasuryLinkEndpoint = GetEndpointType<
  typeof deleteB2bCompanyTreasuryLinkEndpoint
>;
